/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
/* global Blob FileReader */
define([
	'jquery',
	'underscore',
	'./VIPREventTarget',
	'../VisView',
	'../VisEventHandler',
	'dashboard-analytics/visualizations/vipr/VIPR',
	'dashboard-analytics/visualizations/vipr/VIPRUtils',
	'dashboard-analytics/visualizations/vipr/VIPRDataRequestHandler',
	'dashboard-analytics/visualizations/vipr/VIPRCachedDataRequestHandler',
	'dashboard-analytics/visualizations/vipr/properties/VIPRProperties',
	'dashboard-analytics/visualizations/vipr/properties/ColorPropertiesCreator',
	'dashboard-analytics/visualizations/vipr/VIPRConfig',
	'../../../lib/@waca/core-client/js/core-client/utils/BrowserUtils',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../lib/@waca/core-client/js/core-client/utils/PerfUtils',
	'../../../widgets/livewidget/SdkShowError',
	'react-dom',
	'react',
	'./components/CustomVisPreviewMessage',
	'../../../util/ContentUtil',
	'dashboard-analytics/widgets/livewidget/util/VisUtil'
], function(
	$,
	_,
	VIPREventTarget,
	VisView,
	VisEventHandler,
	VIPR,
	VIPRUtils,
	VIPRDataRequestHandler,
	VIPRCachedDataRequestHandler,
	VIPRProperties,
	ColorPropertiesCreator,
	VIPRConfig,
	BrowserUtils,
	stringResources,
	PerfUtils,
	SdkShowError,
	ReactDOM,
	React,
	CustomVisPreviewMessage,
	ContentUtil,
	VisUtil
) {
	'use strict';

	const DEFAULT_THUMBNAIL_PROPERTIES = {
		'optimizeSize': true,
		'gridLines.visible': false,
		'markers.visible': false,
		'widget.legend.size': 0,
		'labels.visible': false,
		'itemAxis.line.visible': false,
		'valueAxis.line.visible': false,
		'valueAxis.ticks.visible': false,
		'itemAxis.ticks.visible': false,
		'itemAxis.gridLines.visible': false,
		'valueAxis.gridLines.visible': false
	};
	/**
	 * List of ignored decorations when generating a thumbnails
	 * @type {string[]}
	 */
	const IGNORED_THUMBNAIL_DECORATIONS = [
		// TODO: right now livewidget does not know what vis-crosshairs is
		// we should probably contribute this list of decorations to be ignored from outside
		'compareLine', // old vis-crosshairs - to be removed when the new crosshair feature flag is removed
		'lines' // new vis-crosshairs
	];

	const VIPRView = VisView.extend({
		initVizPromise: null,

		init: function(options) {
			VIPRView.inherited('init', this, arguments);

			this.$el.attr('id', this.viewId);
			this.$el.addClass('dataview vipr-view');
			this.colorsService = this.dashboard.getFeature('Colors');
			this.visAPI = options.visModel;
			this.predictHandler = this._createPredictHandler(VIPRDataRequestHandler);
			this.highContrastEnabled = $(document.body).hasClass('highcontrast');

			this.internalVisDefinitions = this.dashboard.getFeature('VisDefinitions.internal');

			//Initialize members choosing which title to show (name/titleHtml vs annotation)
			if(this.visModel.getShowTitle()) {
				this._hasUserTitle = true;
				this._hasAnnotationTitle = false;
			}

			this.visController = {};
			this.viprDecorations = {};

			this.dataItemDecorations = {};

			//A list of widgets marked to be destroyed after the current viprWidget control render completes (part of a reRender)
			this._deferredDestroyViprWidgetsList=[];

			this.viprControlRenderInProgress = false;
			this.colorsService.on('theme:changed', this.onThemeChange, this);
			const featureChecker = this.dashboard.getGlassCoreSvc && this.dashboard.getGlassCoreSvc('.FeatureChecker');
			this.isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');
			this.smartAnnotations = this.content.getFeature('SmartAnnotations.deprecated');
		},

		/**
		 * Setup the view and events based on the model
		 * @override
		 */
		createView: function() {
			VIPRView.inherited('createView', this, arguments);

			// Listen for theme changes
			this.visModel.on('change:theme', this.onChangeTheme, this);
			this.visModel.on('change:titleMode', this.onShowTitle, this);
			this.visModel.on('change:customData', this.onChangeCustomData,this);
		},

		/**
		 * Create a predict handler
		 *
		 * @param {Function} Class - class to use for the predict handler
		 * @param {[type]} [handler] - original predict handler, if any; This is used for thumbnails for caching API requests
		 *
		 * @return {object} predict handler instance
		 */
		_createPredictHandler: function(Class, handler) {
			const options = {
				content: this.content,
				visAPI: this.visAPI,
				visView: this,
				logger: this.logger
			};
			if (handler) {
				options.handler = handler;
			}
			return new Class(options);
		},

		/**
		*
		* @param event - payload for a customData model change event
		* 			 event.prevValue - previous selections of custom data
		* 			 event.value - current selections of custom data
		*/
		onChangeCustomData: function(event) {
			const prevSelections = event.prevValue && event.prevValue.selected || [];
			const prevIds = prevSelections.map((decoration) => {
				return decoration.id;
			});
			const prevSelectedCustomData = this.visAPI.getCustomData(prevIds);

			//clear decorations for previous selections;
			prevSelectedCustomData.forEach(function(item) {
				item.setDecoration && item.setDecoration( 'selected', false );
			});

			const curSelectedCustomData = this.visAPI.getDecoratedCustomData('selected');
			// set decorations for current selections;
			curSelectedCustomData.forEach(function(item) {
				item.setDecoration && item.setDecoration( 'selected', true );
			});

			return this.renderVIPRControl({ callRenderComplete: true });
		},

		/**
		 * Create the visualization tabs if the definition require the tabs
		 * The tabs definition consists with the following:
		 * tabs : {
		 *		entries:[{
		 *			id: <unique identifier of the tab>,
		 *			resource: <resource key of the tab display label>
		 *		}]
		 *		default: <identifier of the default select tab entry>
		 * }
		 */
		_createVisTabs: function() {
			if (this._tabs) {
				// ensure we start from scratch
				this._tabs.reset();

				// check the tabs definitions
				const definition = this.visModel.getDefinition();
				if (definition.tabs) {
					// create the tabs
					this._tabs.createTabs(_.map(definition.tabs.entries, (tab) => {
						return {
							id: tab.id,
							label: stringResources.get(tab.resource),
							onChange: function(tabId) {
								this.visModel.ownerWidget.updateVisProperties({
									id: 'actions',
									value: tabId
								});
							}.bind(this)
						};
					}), this.visModel.getPropertyValue('actions') || definition.tabs.default);
				}

				// hide the tabs until necessary to show
				this._tabs.hide();
			}
		},

		/**
		 * return a public API to a decorator for the nth vipr dataset (default dataset=0)
		 * the decorator implements:
		 *		- decorate (decorate the dataset),
		 *		- decorateItem (decorate an item),
		 *		- decoratePoint(decorate a point)
		 *		- clearItemDecoration (clear a given item decoration)
		 *		All decoratorAPIs implement the same updateDecorations method to render the decorations.
		 * Most visualizations have only 1 decoratorAPI (ie: 1 vipr dataset).  Maps, for example have 3.
		 * @param n - the nth decorator (dataset)....default 0
		 * @returns a decoratorAPI for the whole decorator, items or points
		 */
		getDecoratorAPI: function(n) {
			n = n || 0;
			if (!this.viprData || n > this.viprData.getDataSetCount()) {
				return null;
			}
			var oDataSet=this.viprData.getDataSetAt(n);
			if (oDataSet) {
				return {
					decorateItem: (itemIndex, decoration, value) => {
						// Save the decoration so that they are applied when the decoration is refreshed
						// We are only saving the dataitem decorations
						// TODO: Do we need to save datapoints and dataset decorations ??
						if(!this.dataItemDecorations[n]){
							this.dataItemDecorations[n] = {};
						}
						if(!this.dataItemDecorations[n][itemIndex]){
							this.dataItemDecorations[n][itemIndex] = {};
						}

						const dataItemN=oDataSet.getDataItem(itemIndex);
						/**
						 * decorationValue from viprData is usually the same as the value passed in EXCEPT FOR the 'lines' decoration.
						 * The lines decoration returns an array of VIDA decoration instances that are the elements of the line that correspond to the value spec passed in . eg: { baseLine: {}, grabber: {}}
						 */
						const decorationValue =  dataItemN && dataItemN.decorate(decoration, value);
						this.dataItemDecorations[n][itemIndex][decoration] = decorationValue;
						return;
					},
					decoratePoint: (row, decoration, value) => {
						let decorated = null;
						const datapoint = oDataSet.getDataPoint(row);
						if(datapoint && datapoint.decorate){
							decorated =  datapoint.decorate(decoration, value);
						}
						return decorated;
					},
					decorate: (decoration, value) => {
						return oDataSet.decorate(decoration, value);
					},
					clearItemDecoration: (decoration) =>{
						if (this.dataItemDecorations[n]){
							let dataItemIndex;
							for (dataItemIndex in this.dataItemDecorations[n]){
								const dataItemN=oDataSet.getDataItem(dataItemIndex);
								if (dataItemN){
									dataItemN.decorate(decoration, undefined);
								}
								delete this.dataItemDecorations[n][dataItemIndex][decoration];
							}
						}
					},
					updateDecorations: () => this.renderVIPRControl()
				};
			}
			return null;
		},

		/**
		 * return a public interface to all decorators in the visualization
		 * (ie: one decoratorAPI per dataset as an array)
		 * @returns an array of 0 or more getDecoratorAPIs
		 */
		getDecoratorAPIs: function() {
			let decoratorAPIs=[];
			if (this.viprData && this.viprData.getDataSetCount) {
				for (let i=0; i<this.viprData.getDataSetCount(); ++i) {
					const decoratorAPI=this.getDecoratorAPI(i);
					if (decoratorAPI) {
						decoratorAPIs.push(decoratorAPI);
					}
				}
			}
			return decoratorAPIs;
		},

		/**
		 * Handle visualization definition change
		 */
		onChangeDefinition: function() {
			this.initVizPromise = null;
			VIPRView.inherited('onChangeDefinition', this, arguments);
			//Clean Info Indicator and we update the indicator after Data Query is executed
			//for the new view.
			if(this.infoIndicator){
				this.infoIndicator.reset();
			}

			//clean listener flag for vida prop changes
			if(this.propChangeslistener) {
				this.propChangeslistener = undefined;
			}

			// recreate the tabs for the new vistype
			this._createVisTabs();
		},


		/**
		 * Handle removal of the View and/or its embedded viprWidget control.
		 * @param finalRemove - (default) remove is 'final', the embedded viprWidget control is destroyed immediately
		 *		remove can also be called as part of a reRender (final=false).  In this case, the embedded viprWidget is marked
		 *		to be destroyed after the current control render.
		 *		(the embedded control render is async and can't be destroyed mid-render)
		 */
		remove: function(finalRemove=true) {

			this.visModel.off('change:theme', this.onChangeTheme, this);
			this.visModel.off('change:titleMode', this.onShowTitle, this);
			this.visModel.off('change:customData', this.onChangeCustomData,this);

			this.colorsService.off('theme:changed', this.onThemeChange, this);

			this._cleanupVIPRWidget(finalRemove);
			// The super.destroy will clear all members of this class.
			// this is why we do it at the end
			VIPRView.inherited('remove', this, arguments);
		},

		/**
		 * Clean up the VIPR widget and event handler
		 * @param finalRemove - (default) remove is 'final', false - part of reRender (see remove)
		 */
		_cleanupVIPRWidget: function(finalRemove=true) {
			if (this.eventHandler) {
				this.eventHandler.remove();
			}
			if (this.$customVisPreviewMessage) {
				ReactDOM.unmountComponentAtNode(this.$customVisPreviewMessage[0]);
				this.$customVisPreviewMessage.remove();
				this.$customVisPreviewMessage = null;
			}

			this._destroyCustomVisShowError();

			if (this.viprWidget) {
				if (finalRemove && !this.viprControlRenderInProgress) {
					//Destroy immmediately
					this.viprWidget.destroy();
					this.viprWidget = null;
				} else if (this._deferredDestroyViprWidgetsList.indexOf(this.viprWidget) === -1) {
					//Set the viprWidget control to be destroyed at the end of control's async render.
					this._deferredDestroyViprWidgetsList.push(this.viprWidget);
				}
			}
			if(this.viprData && finalRemove) {
				this.viprData.destroy();
				this.viprData = null;
			}
		},

		_destroyCustomVisShowError () {
			if (this.sdkShowError) {
				this.sdkShowError.destroy();
				this.sdkShowError = null;
			}

		},

		/**
		 * This function is called after the viprWidget render to destroy any viprWidget controls that were marked to be destroyed.
		 */
		_deferredDestroyVIPRWidgets: function() {
			_.each(this._deferredDestroyViprWidgetsList, (viprWidgetToDestroy) => {
				viprWidgetToDestroy.destroy();
			});
			this._deferredDestroyViprWidgetsList=[];
		},

		/**
		 * Create a VIPRWidget instance and event handler
		 */
		_createVIPRWidget: function() {
			if (!this.viprWidget) {
				// Create a VIPR container.
				// NOTE. VIPR will literally owns this container and will remove on viprWidget.destroy()
				var containerId = 'VIPR_' + this.viewId;
				this.$el.append($('<div id="' + containerId + '" style="width:100%; height:100%" role="application"/>'));

				var cursors = VIPRUtils.getCursor(this.visAPI.getVisId());

				// The last supported cursor wins. For example, only -webkit-grab works
				// on chrome. This means that if cursors = ['-webkit-grab', 'grab', '-moz-grab'],
				// the style.cursor of the element will be set to '-webkit-grab'.
				if(cursors){
					_.forEach(cursors, (cursor) => {
						this.$el.get(0).style.cursor = cursor;
					});
				}
				//If there is a passed in predictData during initilization, we will create a VIPRCachedDataRequestHandler that caches the predictData
				if(this.predictData){
					this.predictHandler.setLastRequestData('Catalyst',this.predictData);
					this.predictHandler = this._createPredictHandler(VIPRCachedDataRequestHandler, this.predictHandler);
				}
				this.viprWidget = VIPR.createWidget(containerId, this.predictHandler);

				const userProfileService = this.dashboard.getGlassCoreSvc('.UserProfile');
				if (userProfileService) {
					this.viprWidget.formatLocale = userProfileService.preferences.contentLocale;
				}
				this.eventHandler = new VisEventHandler({
					target: new VIPREventTarget({
						$el: this.$el,
						viprWidget: this.viprWidget,
						visAPI: this.visAPI,
						visualization: this.visualization,
						content: this.content
					}),
					dashboardSettings: this.dashboardSettings,
					transaction: this.transactionApi,
					logger: this.logger,
					ownerWidget: this.ownerWidget,
					visAPI: this.visAPI,
					dashboard:this.dashboard
				});
				const vidaThemeToken = this._getVisualizationThemeToken();
				this.setTheme(vidaThemeToken);
			}
		},

		onThemeChange: function () {
			const vidaThemeToken = this._getVisualizationThemeToken();
			this.setTheme(vidaThemeToken);
		},

		setTheme: function(vidaThemeToken) {
			this.viprWidget.setTheme(vidaThemeToken);
		},

		_getVisualizationThemeToken() {
			const themeDefinition = this.colorsService.getThemeDefinition();
			const themeId = themeDefinition.getId();
			const themeDef = VIPRUtils.getVidaThemeDefinition(themeId);
			// Use backup theme definition from theme JSON files
			if(_.isEmpty(themeDef)) {
				const visualizationUIElement = themeDefinition.getUIElement('visualization');
				const reducer = (accumulator, currentValue) => {
					accumulator[currentValue.id] = currentValue.value;
					return accumulator;
				};
				return visualizationUIElement.reduce(reducer, {});
			}
			return themeDef;
		},

		/**
		 * Handle when visualization is ready
		 */
		whenVisControlReady: function() {
			if (!this.initVizPromise) {
				this._createVIPRWidget();
				this.initVizPromise = this.viprWidget.newViz(this.visAPI.getVisId(), 'client')
					.then((vizBundle) => {
						// force to create a new vipr data
						this.viprData = null;
						this.vizBundle = vizBundle;
						return this.visController;
					}).catch((error) => {
						//Remove this checking in R5 for Custom Vis, this is no longer needed
						if(this.visAPI.getVisId() === 'visualizationPreview') {
							this.logger.error(error, this);
							return this.visController;
						} else {
							throw error;
						}
					});
			}
			return this.initVizPromise;
		},

		/**
		 * Create an instance of VIPR data from query data
		 *
		 * @param {object} data - query data
		 *
		 * @return {object} VIPR data
		 */
		createViprData: function(data) {
			const viprData = VIPRUtils.createQueryResultData(data, this.visualization, this.content);
			viprData.hasMoreData = data.hasMoreData;
			viprData.queryThreshold = data.queryThreshold;
			return viprData;
		},

		/**
		 * When there is a connection error we should not deal with preparing data anymore.
		 */
		canRender() {
			return this._hasMappedSlots() && this.isMappingComplete();
		},

		/**
		 * Handle when data is ready
		 */
		whenDataReady: function() {
			return VIPRView.inherited('whenDataReady', this, arguments)
				.then(({ data, sameQueryData }) => {
					let result;
					if (this.canRender() && !this.hasUnavailableMetadataColumns() && !this.hasMissingFilters()) {
						// preserve the query results
						this._dataObject = data;

						if (sameQueryData) {
							result = {
								data: data,
								sameQueryData: sameQueryData
							};
						} else {
							this.predictHandler.resetCustomData();
							this._viprSlotMapping = VIPRUtils.createSlotMapping(this.visualization);
							result = {
								data: data
							};
						}
					} else {
						result = {
							data: {}
						};
					}

					return result;
				});
		},

		/**
		* Set a model property if its valid.
		* @param {String} propertyName - name of the property of interest.
		* @param {String} visId - id of the visualization of interest.
		*/
		_setModelProperty(propertyName, visId) {
			if (this._shouldPropertyBeApplied(propertyName)) {
				const val = this._getPropValueWhenSaving(visId, propertyName);
				var propInfo = {
					id: propertyName,
					value: val
				};
				this.setProperty(propInfo);
			}
		},

		/**
		* set properties needed before data is set.
		*/
		_setPreDataProperties() {
			// Check for such properties in the vipr config
			const visId = this.visAPI.getVisId();
			const config = VIPRConfig.getConfig(visId);
			if (config && config.propertiesToSetBeforeData && config.propertiesToSetBeforeData.length > 0) {
				config.propertiesToSetBeforeData.forEach(propertyName => {
					this._setModelProperty(propertyName, visId);
				});
			}
		},

		/**
		 * handles when data is ready before setting
		 * @param {Object} renderContext
		 */
		whenSetDataReady(renderContext) {
			let result = Promise.resolve(true);
			if (this.canRender() && !this.hasUnavailableMetadataColumns() && !this.hasMissingFilters()) {
				const autoRefresh = !!(renderContext && renderContext.extraInfo && renderContext.extraInfo.queryRefresh);
				// we only care if its the same query IFF the query is an auto refresh query
				// otherwise the query is considered valid to construct a new VIPRData
				const sameQueryData = autoRefresh ? !!renderContext.sameQueryData : false;
				const requiresNewVIPRData = this.viprData ? !sameQueryData : true;
				if (requiresNewVIPRData) {
					// Some properties, such as custom polygons, need to be set before data is set. Do so now.
					this._setPreDataProperties();
					// preserve the query results
					this._dataObject = renderContext.data;

					this._viprSlotMapping = VIPRUtils.createSlotMapping(this.visualization);
					this.viprData = this.createViprData(this._dataObject);

					if (this.viprData) {
						try {
							this.viprWidget.setData(this.viprData, this._viprSlotMapping);
						} catch (e) {
							result = Promise.reject(e);
						}
					} else {
						result = Promise.reject(new Error('Unable to create VIPR data in whenSetDataReady'));
					}
				}
			} else {
				//for cases that that we can render without complete mapping and there is no required slots like schematic, we need to check the slots,
				// if not data item is mapped, so we need to clear data
				const isMappingComplete = this.isMappingComplete() && this.visualization.getSlots().getMappingInfoList().length > 0;
				if (this.isRenderWithoutCompletingMapping() && !isMappingComplete) {
					this.viprWidget.setData(null);
					if (this.infoIndicator) {
						this.infoIndicator.reset();
					}
				}
			}
			return result;
		},

		_getAnnotationMessages(data) {
			return data.getMessageList() || {};
		},

		onAnnotatedResultsReady: function(data) {
			VIPRView.inherited('onAnnotatedResultsReady', this, arguments);
			if (data) {
				// preserve the result object which holds the annotation titles
				this._dataObject = data;

				if (this._dataObject.getQueryResultIdList().length) {
					this.viprData = this.createViprData(this._dataObject);
					this.viprWidget.setData(this.viprData, this._viprSlotMapping);
				}
				this.smartAnnotations.setAnnotationMessages(this._getAnnotationMessages(data));
			}
			return data;
		},

		onShowTitle: function(event) {
			this._hasUserTitle = event.value !== 'noTitle';

			if (this._hasUserTitle) {
				this._hasAnnotationTitle = false;
			}
		},

		/**
		 * VIPR view should use VIPR's built-in animations and not do the default animations.
		 */
		// @Override
		animate: function() {
		},

		_readyForRender: function(){
			return (this.canRender() || this.isRenderWithoutCompletingMapping()) && !this.hasUnavailableMetadataColumns() && !this.hasMissingFilters();
		},

		_updateViprProp(viprPropId, viprPropValue) {
			//we need to explicitly update property value so that dirty flag would be set and users will be aware.
			const options = {
				merge: true,
				remove: false,
				silent: false
			};
			if (viprPropId !== undefined && viprPropValue !== undefined) {
				this.visModel.ownerWidget.updateVisProperties({
					'id': viprPropId,
					'value': viprPropValue && viprPropValue.name ? viprPropValue.name : viprPropValue
				}, options);
			}
		},

		_onViprPropValueChange(viprProp){
			const viprPropId = viprProp.property.getName();
			let viprPropValue = viprProp.property.getValue();
			/*
			* It seems like dashboard is storing the object structures when persisting length properties.
			* This will (e.g. for Length) return an object { value:15, unit:"px" }. However, if vida starts adding
			* internal variables on the object, these will unnecessarily end up in the serialization.
			* If we want to store property values in a spec, need to serialize them by calling 'Prop.toString()'.
			* This will yield e.g. "15px" for the above object.
			* They can then simply be reconstituted into objects by calling Length.parse("15px").
			* This applies to all complex properties, e.g. Color, Palettes, Length, Font etc.
			* NOTE: Have a discussion with Shawn, this is a temporary fix to have the minimum impact on the product,
			* need a generic fix in future
			*/
			if(viprProp.property.type === 'length'){
				viprPropValue = viprPropValue ? viprPropValue.toString() : viprPropValue;
			}
			this._updateViprProp(viprPropId, viprPropValue);
		},

		_allowShowTabs(){
			return this.ownerWidget.allowShowTabs && this.ownerWidget.allowShowTabs();
		},

		_renderTabs() {
			// ensure the a tabs are visible with the selected tab
			if (this._tabs.getTabsCount() > 0 && this._allowShowTabs()) {
				this._tabs.show();
				this._tabs.selectTab(this.visModel.getPropertyValue('actions'));
			} else {
				this._tabs.hide();
			}
		},

		placeAt: function() {
			VIPRView.inherited('placeAt', this, arguments);
			// Intentionally left out of init function [CADBLW-7546].
			this._createVisTabs();
		},

		/**
		 * Render the VIPR visualization
		 */
		render: function(renderInfo) {
			VIPRView.inherited('render', this, [renderInfo, /*callRenderComplete*/false]);

			this._destroyCustomVisShowError();

			if (!this.viprWidget) {
				return Promise.resolve();
			}

			if(this._renderCustomVisContext()) {
				renderInfo.widgetSize.height = this.$el.closest('.liveWidgetContent').height();
			}

			this.resizeToWidget(renderInfo);
			if (!this._readyForRender()) {
				return VisUtil.validateVisDefinition(this.content, this.dashboard, { visId: this.ownerWidget.model.visId })
					.then((isValid) => {
						if (isValid) {
							this.renderIconView();
						} else {
							const $originalContentBeforeError = this.$el.children();
							$originalContentBeforeError.detach();

							this.sdkShowError = new SdkShowError(this.$el[0], stringResources.get('extVisNoSDKConnection'), stringResources.get('extVisNoSDKPreviewMsg'), 'disconnect');
							this.sdkShowError.showError();
						}
					});
			}

			this._renderTabs();

			this.removeIconView();

			//Listen for property value change if any, add a flag to prevent registering the same listener multiple times
			if (!this.propChangeslistener) {
				this._registerListenerForPropChanges();
			}

			return this._applySavedModelProperties(renderInfo)
				.then(() => {
					if (!this.visModel.getRenderSequence().isActive()) {
						return;
					}
					var augmentations = this.visAPI.ownerWidget.model.augmentation; //TODO: add getAugmentations to visAPI
					if (augmentations && augmentations.length > 0) {
						for (var i = 0; i < augmentations.length; i++) {
							var augmentation = augmentations[i];
							this._appendDVI(augmentation);
						}
					}

					return this.renderSelected(renderInfo);
				});
		},
		/**
		* Render items that are specific to custom visualizations (ex. Custom Vis Preview Message)
		*/
		_renderCustomVisContext: function() {
			if(this.ownerWidget.model.visId === 'visualizationPreview') {
				const iconsFeature = this.dashboard.getFeature('Icons');
				const $parent = $(this.$el.closest('.widgetContentWrapper'));
				if($parent.find('.customVisPreviewMessage').length < 1) {
					this.$customVisPreviewMessage = $('<div>');
					this.$customVisPreviewMessage.addClass('customVisPreviewMessage');
					$parent.prepend(this.$customVisPreviewMessage);
					ReactDOM.render(<CustomVisPreviewMessage iconsFeature={iconsFeature}/>, this.$customVisPreviewMessage[0]);
				}
				return true;
			}
			return false;
		},

		reveal: function() {
			if (BrowserUtils.isIE11() ||
				!this._readyForRender() ||
				!this._getEntryDurationProperty() ||
				!this.viprData ){
				this.visAPI.renderCompleteBeforeAnimation();
				return Promise.resolve();
			}
			// reveal is a render which triggers the widget's entry level animation.
			// This is done by clearing and resetting the data as there is no API for it atm.

			let emptyData = this.viprWidget.processData(this.viprData, this._viprSlotMapping).empty();
			this.viprWidget.setData(emptyData, this._viprSlotMapping);
			this._applyEntryDurationProperty('zero');
			return this.renderVIPRControl().then(() => {
				this._applyEntryDurationProperty('model');
				this.viprWidget.setData(this.viprData, this._viprSlotMapping);
				this.renderVIPRControl();
				return {
					// renderControlApi is a thenable object (has a then method),
					// but it's not a pure promise since it has other methods added by VIDA.
					// if we return it directly those api's get lost.
					renderControlApi: this.renderControlApi
				};
			}).catch(e => {
				this.viprWidget.setData(this.viprData, this._viprSlotMapping);
				throw e;
			});
		},

		getCurrentViewSelector: function() {
			return this.eventHandler;
		},

		/**
		 * @private
		 * TODO: SmartsExecution requests are using a legacy QueryResultsObject.
		 * Annotations should be moved to a feature and updated with proper api's for decorations.
		 * In the meantime, the response value indexes and decorations can be accessed via the "pt" array and "deco" member.
		 * @param {Object} value - datapoint value
		 * @return {String} unique string representing the datapoint
		 */
		_getDatapointKeyFromSmartAnnotationsResult: function(value) {
			// generate a unique key that consists of the category indices
			// without the ordinal values
			// INPUT: [2, 1, {v: 886.970000016}]
			// OUTPUT: '2,1'
			return value ? _.filter(value.pt, (pt) => { return typeof pt === 'number'; }).toString() : '';
		},

		_decorateDatapoints: function(dataset) {
			const selection = this.getSelector().getSelection(ContentUtil.getColumnIdList((dataset.slots || this.visualization.getSlots().getMappedSlotList()), 'attribute'));
			for(let index = 0; index < dataset.getDataPointCount(); index++){
				let datapoint = dataset.getDataPoint(index);
				if (datapoint.decorate && datapoint.hasDecoration) {
					// Decorate only if needed. Avoid re-decorating with the same decorations.
					if (selection && selection.isSelected(datapoint.getTupleItems())) {
						datapoint.decorate('selected', true);
					} else if (datapoint.hasDecoration('selected')) {
						datapoint.decorate('selected', false);
					}
				}
			}

			return !!selection;
		},

		_decorateDataItems: function(dataset, datasetIndex) {
			let decorated = false;
			for (let iDataItem = 0; iDataItem < dataset.getDataItemCount(); iDataItem++) {
				const dataItem = dataset.getDataItem(iDataItem);

				// when the query result data decorations are updated the VIPR objects need to reapply the decos
				dataItem.resetDecorations();

				// Maintain any decoration that were added using the widget decorator API
				if (this.dataItemDecorations[datasetIndex] && this.dataItemDecorations[datasetIndex][iDataItem]){
					const dataItemDecorations = this.dataItemDecorations[datasetIndex][iDataItem];
					let decoName;
					for(decoName in dataItemDecorations){
						if(dataItemDecorations[decoName] !== null && dataItemDecorations[decoName] !== undefined){
							dataItem.decorate(decoName, dataItemDecorations[decoName]);
						}
					}
				}

				// decorate selection
				if (dataItem.getType() === 'cat') {
					// collect the ItemIds (single or nested)
					let itemIds = [];
					let aItemClassSet = dataItem.getItemClassSet();

					for (let iItemClass = 0; iItemClass < aItemClassSet.getItemClassCount(); iItemClass++) {
						itemIds.push(aItemClassSet.getItemClass(iItemClass).getUniqueName());
					}

					const selection = this.getSelector().getSelection(itemIds);
					const selectedVIPRItem = {};
					const allVIPRItem = {};
					for (let iTuple = 0; iTuple < dataItem.getTupleCount(); iTuple++) {
						const tuple = dataItem.getTuple(iTuple);

						const tupleItems = [];
						for (let iTupleItem = 0; iTupleItem < tuple.getItemCount(); iTupleItem++) {
							const item = tuple.getItem(iTupleItem);
							tupleItems.push(item.item);
							allVIPRItem[item.getUniqueName()] = item;
						}

						// unlike the datapoint decorations, each legend items need to be explictly selected/unselected
						if (selection) {
							const isTupleSelected = !!selection.isSelected(tupleItems);
							tuple.decorate('selected', isTupleSelected);

							isTupleSelected && tuple.items.forEach(VIPRItem=>{
								selectedVIPRItem[VIPRItem.getUniqueName()] = VIPRItem;
							});

							decorated = true;
						} else {
							tuple.decorate('selected', false);
						}

					}

					// handle VIPRItem selection
					Object.values(selectedVIPRItem).forEach(viprItem=>viprItem.decorate('selected', true));
					Object.keys(allVIPRItem).filter(key=>!selectedVIPRItem[key]).forEach(itemKey=>{
						allVIPRItem[itemKey].decorate('selected', false);
					});

				}
			}
			return decorated;
		},

		decorateData: function(
			viprData,
			decorations = {
				dataPoints: true,
				dataItems: true,
				selection: true,
				annotations: true
			}
		) {
			let dataset;
			for (let i = 0; i < viprData.getDataSetCount(); i++) {
				dataset = viprData.getDataSetAt(i);
				if (dataset) {
					let dataItemsDecorated = false;
					if (decorations.dataItems) {
						dataItemsDecorated = this._decorateDataItems(dataset, i);
					}

					let datapointsDecorated = false;
					if (decorations.dataPoints) {
						datapointsDecorated = this._decorateDatapoints(dataset);
					}

					if (decorations.selection) {
						// The hasSelection decoration tells the visualization if there is any selection in play, and changes
						// how items are rendered by default.
						dataset.decorate('hasSelection', datapointsDecorated || dataItemsDecorated);
					}

					if (decorations.annotations) {
						// SmartAnnotations not available for all integrations (ie. PA)
						const annotations = this.smartAnnotations && this.smartAnnotations.getEnabledAnnotations();
						// Check for annotation type
						const meaningfulDiff = annotations ? _.where(annotations, { type: 'MEANINGFUL_DIFFERENCES' }) : [];
						dataset.decorate('hasAnnotations', !!meaningfulDiff.length);
					}
				}
			}
		},

		renderSelected: function(renderInfo) {
			if (!this.visModel.getRenderSequence().isActive()) {
				return Promise.resolve();
			}
			if (this.viprData) {
				this.decorateData(this.viprData);
			}
			// restore the visualization state
			if (this.eventHandler) {
				this.eventHandler.restoreState();
			}
			const perfMarkName = `WidgetRender_${this.visModel.getWidgetId()}`;
			PerfUtils.createPerformanceMark({
				'component': 'dashboard',
				'name': perfMarkName,
				'state': 'start'
			});

			return this.renderVIPRControl({ renderInfo : renderInfo, callRenderComplete: true }).then(() => {
				PerfUtils.createPerformanceMark({
					'component': 'dashboard',
					'name': perfMarkName,
					'state': 'end'
				});
				if (!this.visModel.getRenderSequence().isActive()) {
					return;
				}
			});
		},

		renderVIPRControl(options = { callRenderComplete: false }) {
			const { renderInfo, callRenderComplete } = options;
			// complete the previous render if it's still running.
			if( this.renderControlApi && this.renderControlApi.complete ){
				this.renderControlApi.complete();
			}
			this.viprControlRenderInProgress = true;
			this.renderControlApi = this.viprWidget.render('client');
			this.renderControlApi.onprogress = _message => {
				if ( _message.status === 'animating' ) {
					this.visAPI.renderCompleteBeforeAnimation();
				}
			};
			return this.renderControlApi.then(() => {
				callRenderComplete && this.visAPI.renderComplete(renderInfo);
				this._deferredDestroyVIPRWidgets();
				this.viprControlRenderInProgress = false;
			}).catch((e) => {
				throw e;
			});
		},

		/**
		 * check if the value of a property matched expected
		 * @param  {String} propertyName
		 * @param  {object} expectedValue
		 * @return {boolean} true if it matches
		 */
		doesVisPropertyMatchExpected: function (propertyName, expectedValue) {
			if(propertyName && this.viprWidget){
				return VIPRUtils.doesConfigPropertyMatchExpected(this.visualization.getDefinition().getId(), propertyName, expectedValue);
			}
			return false;
		},

		/**
		 * check if auto binning is set for this view.
		 * @return {boolean} true if anto binning can be appied for this view
		 */
		canApplyAutoBin: function () {
			const doesConfigSupportAutobinning = VIPRUtils.canApplyAutoBin(this.visualization.getDefinition().getId());

			const definition = this.visualization.getDefinition();
			const slots = this.visualization.getSlots().getMappedSlotList();
			const slotOneRole = 'explanatory';
			const colorSlotRole = 'group';
			let doesWidgetHaveBinnableDataItem = false;
			if (definition.getProperty('canApplyBinning')) {
				_.find(slots, slot => {
					let role = slot.getDefinition().getRole();
					if (slot.getDefinition().getType() !== 'ordinal' && (role === slotOneRole || role === colorSlotRole)) {
						const fact = slot.getDataItemList().find(dataItem => {
							return dataItem.getMetadataColumn().getType() === 'fact';
						});
						doesWidgetHaveBinnableDataItem = !!fact;
						return doesWidgetHaveBinnableDataItem;
					}
				});
			}

			return doesConfigSupportAutobinning && doesWidgetHaveBinnableDataItem;
		},

		/**
		 * @returns the default binning config  for this widget.
		 */
		getBinningConfig: function () {
			return VIPRUtils.getBinning(this.visualization.getDefinition().getId());
		},

		/**
		* @returns the default palette from the colorsService
		*/
		_getDefaultContinuousPaletteName: function() {
			return this.colorsService.getDefaultPaletteName('HeatPalette');
		},

		/**
		* @returns the default palette from the colorsSerivce
		*/
		_getDefaultCategoricalPaletteName: function() {
			return this.colorsService.getDefaultPaletteName('ColorPalette');
		},

		/**
		* @param palette - palette property item describing a specific vipr palette
		* @param possibleDataSlots - all possible data slots for the current vis type.
		* @returns a continuous heat palette description needed to set a palette resolver in VIPR
		* eg.
		* {
		*	type: "cont"
		*	colors: description of colors (either continuous or categorical)
		* defaultIndex: index into the palette for an element when the color by slot is empty.
		* }
		*/
		_getContinuousColorPaletteDesc: function(viprPalette, possibleDataSlots) {
			// Get either the overriden palette or the default palette
			const colorPropsCreatorInstance = ColorPropertiesCreator.getInstance();
			var dataLayerId = colorPropsCreatorInstance.getDataLayerIdFromPaletteAndSlots(viprPalette, possibleDataSlots);
			var propertyName = colorPropsCreatorInstance.getContinuousColorPalettePropertyNameWithLayer(dataLayerId);
			var paletteName = this.visModel.getPropertyValue(propertyName) || this._getDefaultContinuousPaletteName();

			return this.colorsService.getPalette({
				paletteId: paletteName,
				type: 'HeatPalette'
			}).then((palette) => {
				var paletteDef = {
					type: ''
				};
				if (palette) {
					paletteDef.type = 'cont';
					paletteDef.dataLayerId = dataLayerId;
					var colors = [];
					/* VIPR had an original issue with the typical way we express our color
					* stops. We could wait for their fix (its on its way) or work around it.
					* This is the work around. We want to change our stops to return in this
					* format:
					*	 ["color %", "color %", ...]
					*/
					// Order the colors depending on the heat scale color order property.
					var heatScalePropId = colorPropsCreatorInstance.getHeatScalePalettePropertyNameWithLayer(dataLayerId);
					var colorOrder = this.visModel.getPropertyValue(heatScalePropId);
					const paletteFills = palette.fills;
					const paletteLength = paletteFills.length;
					for (var index = 0; index < paletteLength; index++) {
						// If the colorOrder is dark to light then reverse the colors.
						var color = colorOrder !== 'DarkerForLowerValue' ? paletteFills[index].fill : paletteFills[paletteLength - index - 1].fill;
						var at = paletteFills[index].at;
						var colorStr = color + ' ' + String(at*100)+'%';
						colors.push(colorStr);
					}
					paletteDef.colors = colors;
				}
				return paletteDef;
			});
		},

		/**
		* @param {Object} palette - viprWidget palette
		* @returns secondary color index is needed, undefined otherwise.
		*/
		_getSecondaryColorIndexForCatColorPalettes: function(palette) {
			let defaultSecondaryColor;
			let secondaryColorProp;
			const singlePaletteProps = VIPRUtils.getSinglePaletteProperties(this.visAPI.getVisId());
			if (singlePaletteProps){
				Object.values(singlePaletteProps).forEach(propDesc => {
					if (propDesc.defaultColorIndex === 1){
						secondaryColorProp = propDesc;
					}
				});
			}
			if (secondaryColorProp) {
				let secondaryValue = this.visModel.getPropertyValue(secondaryColorProp.id);
				defaultSecondaryColor = secondaryValue !== null ? secondaryValue : 1;
				// Ensure defaultIndex is not above the number of colors in the current palette
				defaultSecondaryColor = defaultSecondaryColor < palette.fills.length ? defaultSecondaryColor : 1;
			}
			return defaultSecondaryColor;
		},

		/**
		* @param {Object} palette - viprWidget palette
		* @returns {Object} primary and secondary color index
		*/
		_getColorIndicesForCatColorPalettes: function(palette, propertyName) {
			let defaultIndex = this.visModel.getPropertyValue('defaultPaletteIndex') || this.visModel.getPropertyValue(propertyName + '_defaultIndex') || 0;
			// Ensure defaultIndex is not above the number of colors in the current palette
			defaultIndex = defaultIndex < palette.fills.length ? defaultIndex : 0;

			let defaultSecondaryColor = this._getSecondaryColorIndexForCatColorPalettes(palette);

			return {
				defaultIndex: defaultIndex,
				secondaryIndex: defaultSecondaryColor
			};
		},

		/**
		* @param viprPalette - viprWidget.palettes item describing a specific vipr palette
		* @param possibleDataSlots - all possible data slots for the current vis type.
		* @returns a categorical palette description needed to set a palette resolver in VIPR
		* eg.
		* {
		*	type: "cat"
		*	colors: description of colors (either continuous or categorical)
		* defaultIndex: index into the palette for an element when the color by slot is empty.
		* }
		*/
		_getCategoricalColorPaletteDesc: function(viprPalette, possibleDataSlots) {
			// Get either the overriden palette or the default palette
			const colorPropsCreatorInstance = ColorPropertiesCreator.getInstance();
			const dataLayerId = colorPropsCreatorInstance.getDataLayerIdFromPaletteAndSlots(viprPalette, possibleDataSlots);
			const propertyName = colorPropsCreatorInstance._getCategoricalColorPalettePropertyNameWithLayer(dataLayerId);
			const paletteName = this.visModel.getPropertyValue(propertyName) || this._getDefaultCategoricalPaletteName();

			return this.colorsService.getPalette({
				paletteId: paletteName,
				type: 'ColorPalette'
			}).then((palette) => {
				var paletteDef = {
					type: '',
					colors: ''
				};

				const colorIndices = this._getColorIndicesForCatColorPalettes(palette, propertyName);

				if (palette) {
					paletteDef.type = 'cat';
					paletteDef.colors = palette.fills;
					paletteDef.defaultIndex = colorIndices.defaultIndex;
					paletteDef.secondaryColorIndex = colorIndices.secondaryIndex;
				}
				return paletteDef;
			});
		},

		/**
		* @param viprPalette - viprWidget.palettes item describing a specific vipr palette
		* @param possibleDataSlots - all possible data slots for the current vis type.
		* @returns a palette description needed to set a palette resolver in VIPR
		* eg.
		* {
		*	type: "" // cat or cont
		*	colors: description of colors (either continuous or categorical)
		* defaultIndex: index into the palette for an element when the color by slot is empty. This is only supplied for categorical palettes.
		* }
		*/
		_getPaletteDescForPalette: function(viprPalette, possibleDataSlots) {
			let getColorPaletteDesc;
			switch (viprPalette.paletteType) {
				case 'cont':
				case 'customcont':
					getColorPaletteDesc = this._getContinuousColorPaletteDesc.bind(this);
					break;
				case 'cat':
				case 'single':
				case 'customcat':
					getColorPaletteDesc = this._getCategoricalColorPaletteDesc.bind(this);
					break;
				default:
					getColorPaletteDesc = null;
			}

			return getColorPaletteDesc ? getColorPaletteDesc(viprPalette, possibleDataSlots).then((desc) => {
				const theme = this.colorsService.getThemeDefinition();
				desc.isLegacy = !!theme.getProperty('legacy');
				desc.type = viprPalette.paletteType;
				return desc;
			}) : Promise.resolve({});
		},

		_getEntryDurationProperty: function() {
			let name = null;
			const configuration = VIPRConfig.getConfig(this.visAPI.getVisId());
			if (configuration && configuration.entryDurationProperty) {
				name =  configuration.entryDurationProperty;
			}else if( this.viprWidget.properties.get('effect.duration')){
				const val = VIPRUtils.getOverridenDefaultForProperty(this.visAPI.getVisId(), 'effect.duration').defaultValue;
				if( val !== null && val >=0 ){
					// backward compatible value, if we actually have a value to set (I.e defaultValue is configured)
					name = 'effect.duration';
				}
			}
			return name;
		},

		_isDurationPropertyValuePropValid(value) {
			return !(value === undefined || value < 0);
		},

		_applyEntryDurationProperty: function(durationOverwrite){
			let propertyName = this._getEntryDurationProperty();
			if( !propertyName ){
				return;
			}

			/*
			* 3 possible values for the duration:
			* 1) zero - set the value to 0 so there is no animation
			* 2) model - ask the model for the value to use. If the user has set a value
			* then it will be used.
			* 3) if value is still undefined, check our local config for overrides.
			*
			* zero is a special case where we set all the animations values to zero.
			* for now this only includes effect.duration.
			*
			*/
			let value;
			let effectValue = VIPRUtils.getOverridenDefaultForProperty(this.visAPI.getVisId(), 'effect.duration').defaultValue || 0;
			if( durationOverwrite === 'zero' ) {
				value = 0;
				effectValue = 0;
			}else if( durationOverwrite === 'model') {
				value = this.visModel.getPropertyValue(propertyName);
			}
			//default (and if model has no value) - check for config override.
			if (!this._isDurationPropertyValuePropValid(value)) {
				value = VIPRUtils.getOverridenDefaultForProperty(this.visAPI.getVisId(), propertyName).defaultValue;
			}
			// If we still don't have a value, forget it, we'll use the default.
			if (this._isDurationPropertyValuePropValid(value)) {
				this.setProperty({ id: 'effect.duration', value: effectValue });
				this.setProperty({ id: propertyName, value: value });
			}
		},

		/**
		* Single palettes are handled differently than categorical or continuous
		* For one, there is always two of them. Secondly, both the single palette
		* resolvers must use the same categorical palette (or at least same colors)
		*/
		_applySavedSingleColorPalette: function() {
			// Create the categorical palette resolver needed for the single palette.
			return this._getCategoricalColorPaletteDesc().then((catPaletteDesc) => {
				return VIPRProperties.getInstance().setSinglePaletteResolvers(this.viprWidget, catPaletteDesc);
			});
		},

		/**
		* VIPR has some properties in its bundles that are handled in a different way.
		* We have to protect against setting them using the properties API as there
		* could be unexpected side affects.
		* @param property - specific vipr defined property in question
		* @returns true iff the property should be applied
		* @// TODO: replace this list with dontApply: true in the VIPR config
		*/
		_shouldPropertyBeApplied: function(propertyName) {
			let blackList = [
				'legend.display',
				'legend.position',
				'effect.duration',
				'effect.entry.line.duration',
				'effect.entry.bar.duration'
			];
			blackList = blackList.concat(this._getDontApplyProperties());
			return !blackList.includes(propertyName);
		},

		_getDontApplyProperties() {
			const dontApplyProperties = [];
			//Since the VIDA 2.10 change, VIDA is now treating color palettes like properties.
			//They are now included as part of the VIPRWidget properties.
			//Therefore, we dont want to override the palettes with the default value
			//Add them to the list of dontApplyProperties
			const colorPalettes = VIPRUtils.getPalettes(this.viprWidget.properties, this.visualization.getDefinition().getId());
			colorPalettes.forEach(palette => {
				if (palette.name !== 'colors.series') { //colors.series is marked in VIPRConfig to not set the default value
					dontApplyProperties.push(palette.name);
				}
			});
			return dontApplyProperties;
		},

		/**
		* Set the palette resolvers for the categorical and continuous palettes.
		*/
		_applySavedNonSingleColorPalette: function () {
			const promises = [];

			// Only apply the palettes associated with the current visualization.
			var possibleDataSlots = this.visualization.getSlots().getSlotList();
			VIPRUtils.getPalettes(this.viprWidget.properties, this.visualization.getDefinition().getId()).forEach(palette => {
				promises.push(this._getPaletteDescForPalette(palette, possibleDataSlots).then((paletteDef) => {
					// Make sure there is avalid defintion for the palette type.
					if (paletteDef) {
						VIPRProperties.getInstance().setPaletteResolvers(palette, paletteDef, possibleDataSlots, this.visAPI.getFredIsRed());
					}
					return paletteDef;
				}));
			});

			return Promise.all(promises);
		},

		/**
		* Apply all palette properties relevant to the current visualization
		*/
		_applySavedColorPalette: function() {
			var isThereSinglePalettes = VIPRUtils.getSinglePalettes(this.viprWidget.properties, this.visualization.getDefinition().getId()).length > 0;
			if (isThereSinglePalettes) {
				return this._applySavedSingleColorPalette();
			} else {
				return this._applySavedNonSingleColorPalette();
			}
		},

		/**
		* VIPR will optimize the size of a visualization when told (i.e. remove axis labels
		* when it thinks more space should be given to the visual elements). We want
		* to enable this be default or if we are showing small multiples. It should
		* only be disabled for upgraded content.
		*/
		_setOptimizedSizeProperty: function(visId) {
			const propertyId = 'optimizeSize';
			let defaultValue = this._getPropValueWhenSaving(visId, propertyId);
			if (!defaultValue) {
				// Do we have small multiple mapped slots?
				const aMultipliers = _.filter(this.visualization.getSlots().getMappedSlotList(), function(dataSlot) {
					return dataSlot.getDefinition().getProperty('multiplier') === true;
				});
				// If we have small multiple mapped slots then lets optimize.
				if (aMultipliers && aMultipliers.length > 0 || this.visAPI.isOptimizeForSize()) {
					defaultValue = true;
				}
			}
			var propInfo = {
				id: propertyId,
				value: (defaultValue === undefined) || (defaultValue === null) ? true: defaultValue // true by default
			};

			this.setProperty(propInfo);
		},

		/**
		* @return the default background color in hex
		*/
		_getDefaultBackgroundColorInHex: function() {
			var defaultSpecColor = this.colorsService.getPropertyForUIElement('widget', 'backgroundColor').value;
			// The value may be equal to a simple hex or variable name or it may be an object which we have to dig into a little more.
			if (_.isObject(defaultSpecColor)) {
				defaultSpecColor = defaultSpecColor.color;
			}
			// I love themes and the theme definition object <insert sarcasm smirk here>. The color may come back
			// as a nice hex value or as a variable that needs to be mapped. If its a variable it will begin with
			// a '$' and we have to map it to get the hex value.
			if (defaultSpecColor.substring(0, 1) === '$') {
				defaultSpecColor = this.colorsService.getValueForVariable('Color', defaultSpecColor.substr(1));
			}
			// Ensure we have a valid hex value or transparent
			if ((defaultSpecColor.substring(0, 1) === '#' && !isNaN(parseInt(defaultSpecColor.substr(1), 16))) || (defaultSpecColor === 'transparent')) {
				return defaultSpecColor;
			}
			return null;
		},

		/**
		* Set the background color of the viprwidget
		*/
		_setBackgroundColor: function() {
			// Override the vida theming background colors based on the fillColor of owner widget
			const backgroundColorProps = VIPRUtils.getBackGroudColorPropertyIdList().map( prop => ({
				id: prop,
				value: undefined
			}));
			if (this.highContrastEnabled) {
				backgroundColorProps.forEach(prop => this.setProperty(prop));
			} else {
				const fillColor = this.visModel.ownerWidget.model.fillColor;
				const hexFillColor = fillColor ? this.colorsService.getHexColorFromDashboardColorSet(fillColor) : this._getDefaultBackgroundColorInHex();

				backgroundColorProps.forEach(prop => {
					prop.value = hexFillColor;
					this.setProperty(prop);
				});
			}
		},

		_getColorToSave(savedValue, isCustomVis) {
			let colorToSave;
			if (_.isString(savedValue)) {
				if (this.colorsService.isCustomColor(savedValue)) {
					this.colorsService.addCustomColor(this.colorsService.getHexColorFromClassName(savedValue));
				}
				colorToSave = this.colorsService.getHexColorFromDashboardColorSet(savedValue);
			} else {
				if (isCustomVis) {
					savedValue = this.colorsService.makeSureColorIsValidInModel(savedValue);
				}
				colorToSave = savedValue;
			}
			return colorToSave;
		},

		/**
		* Apply the color set property of the specified property id
		* @param {String} visId - visualization type id
		* @param {String} propId - id of the color set property to set
		*/
		_applyColorSetProperty(visId, propId, isCustomVis) {
			const savedValue = this._getPropValueWhenSaving(visId, propId);

			// Only apply the color if we have something saved
			if (savedValue) {
				let colorToSave = this._getColorToSave(savedValue, isCustomVis);
				// Put the information in a structure Vida understands
				let colorInfo = {
					id: propId,
					value: colorToSave
				};
				this.setProperty(colorInfo);
			}

		},

		/**
		* @param {String} visId - id of the current visualization.
		* @param {Array} properties - array of property names for the specified vis
		*/
		_applyColorSetProperties(visId, properties, isCustomVis) {
			properties.forEach(propertyName => {
				const property = this.visModel.getPropertyById(propertyName);
				if (property && property.colorClass) {
					this._applyColorSetProperty(visId, propertyName, isCustomVis);
				}
			});
		},

		_getLengthPropWithPxUnit(value) {
			if (typeof value !== 'undefined' && value !== null) {
				return value && value.endsWith && value.endsWith('px') ? value : value + 'px';
			}
			return null;
		},

		_applyTargetMarkerThicknessProperties: function(visId) {
			const targetThickness = this._getPropValueWhenSaving(visId, 'target.marker.thickness');
			if (VIPRProperties.getInstance().isTargetThicknessInputValid(targetThickness)) {
				let valueToSet = this._getLengthPropWithPxUnit(targetThickness);
				let targetThicknessInfo = {
					id: 'target.marker.thickness',
					value: valueToSet
				};
				this.setProperty(targetThicknessInfo);
			}
		},

		/**
		* Apply any saved layer transparencies.
		* Only applies to visualizations that support layers.
		*/
		_applySavedLayerTransparencies: function() {
			var configuration = VIPRConfig.getConfig(this.visAPI.getVisId());
			if (configuration && configuration.supportsLayers) {
				configuration.layerDescriptions.forEach(function(layerDesc) {
					var val = this.visModel.getPropertyValue(layerDesc.colorRelatedProps.transparencyProp);
					if ((val !== undefined) && (val !== null)) { // Can be false
						var propInfo = {
							id: layerDesc.colorRelatedProps.transparencyProp,
							value: val * 100 // Property is saved from 0.0 to 1.0, we want 0-100
						};
						this.setProperty(propInfo);
					}
				}.bind(this));
			}
		},

		/**
		* @returns true iff the value is not undefined.
		*/
		_isPropValueValid(value, propDesc) {
			const isPropValidByDescCheck = propDesc.checkForValidValue ? propDesc.checkForValidValue(value) : true;
			return isPropValidByDescCheck && (value !== undefined);
		},

		/**
		* @returns true if the prop is inactive, readonly and has a valid default value
		*/
		_isValidReadOnlyProperty(propDesc) {
			return (propDesc &&
				propDesc.isActive === false &&
				propDesc.isReadOnly === true &&
				this._isPropValueValid(propDesc.defaultValue, propDesc));
		},

		/**
		* So, Vida will provide us with defaults for value and default value among
		* other things. We need these for properties such as themeable props. One
		* thing we don't need is all the vida property methods that come alogn with
		* the property. So, we want to overide our model prop with wanted object
		* properties only. Note, calling this will actually override the property
		* in the model!
		* @param {String} propertyName - id of the property of interest.
		*/
		overrideWithVidaProperty(propertyName) {
			const modelProp = this.visModel.getPropertyById(propertyName);
			let vidaProp = this.viprWidget.properties.get(propertyName);
			if (vidaProp && modelProp) { // Should never happen but Vida is missing some deprecated props right now.
				const propsWeWantFromVidaProp = {
					active: vidaProp.active,
					caption: vidaProp.caption,
					defaultValue: vidaProp.defaultValue,
					description: vidaProp.description,
					name: vidaProp.name,
					type: vidaProp.type,
					possibleValues: vidaProp.possibleValues
				};
				if(modelProp.value !== undefined) {
					_.extend(propsWeWantFromVidaProp,{ value: vidaProp.value });
				}
				if(!_.isEmpty(vidaProp.tags)) {
					_.extend(propsWeWantFromVidaProp,{ tags: vidaProp.tags });
				}
				_.defaults(modelProp, propsWeWantFromVidaProp);
			}
		},

		/**
		* When saving the values for a property it can be a little tricky. For example,
		* if you set the stacked parameter to false in an area chart and then change the
		* vis type to a stacked bar chart we still want it to stack. So, we should
		* check the property to see if its active. If it isn't then we have to do a
		* little priority checking.
		* @param {String} visId - bundle id
		* @param {String} propertyName - name of the property of interest
		* @returns value of the property to use when saving.
		*/
		_getPropValueWhenSaving(visId, propertyName) {
			let result = null;
			const propDesc = VIPRUtils.getOverridenDefaultForProperty(visId, propertyName);

			/*
			* Do we have a readonly property (i.e. one that has a default and can't be
			* overriden by a user or programmatically)? If so, use the defaultValue
			*/
			if (this._isValidReadOnlyProperty(propDesc)) {
				result = propDesc.defaultValue;
			} else {
				// Get any vida defaults we may need.
				this.overrideWithVidaProperty(propertyName);

				// check if the content is able to supply the property value
				// if not for non-public properties, refer to the properties model
				let propValue = this.content.getPropertyValue(propertyName);
				if (propValue === undefined) {
					// Now we should look at the model. If there is a valid (non undefined) value there
					// use it, otherwise check for the defaultValue in the config.
					propValue = this.visModel.getPropertyValue(propertyName);
				}
				if (this._isPropValueValid(propValue, propDesc)) {
					result = propValue;
				} else {
					result = propDesc.defaultValue;
				}
			}

			return result;
		},

		_registerListenerForPropChanges() {
			const listenForPropChanges = this.visAPI.getListenForPropChangesFromDefinition();
			if (listenForPropChanges) {
				_.each(listenForPropChanges,(prop) => {
					this.viprWidget.properties.get(prop).on( 'value', this._onViprPropValueChange.bind(this));
				});
				//should update the flag when there are actual listeners have been registered
				this.propChangeslistener = true;
			}
		},

		/**
		* When a visualization is about to be rendered we want to ensure that the
		* visualization properties are all set to the user overriden value, if any.
		*/
		_applySavedModelProperties: function(renderInfo) {
			return this._applySavedColorPalette().then(() => {
				const visId = this.visAPI.getVisId();
				const configuration = VIPRConfig.getConfig(visId);
				let configInclude = configuration && configuration.config && configuration.config.include ? configuration.config.include : [];
				if (configuration && configuration.isCustomVis && configuration.bundleInclude) {
					configInclude = configuration.bundleInclude;
				}
				configInclude.forEach(propertyName => {
					this._setModelProperty(propertyName, visId);
				});
				this._applySavedLayerTransparencies();

				if(renderInfo && renderInfo.extraInfo && renderInfo.extraInfo.preserveDrawingBuffer === true){
					this.setProperty({ id: 'webGL.preserveDrawingBuffer', value: true });
				}else{
					this.setProperty({ id: 'webGL.preserveDrawingBuffer', value: false });
				}
				// Now that the default value for optimize size is set, override it to what we really want.
				this._setOptimizedSizeProperty(visId);
				this._setBackgroundColor();
				this._applyEntryDurationProperty(renderInfo && renderInfo.extraInfo && renderInfo.extraInfo.entryDuration);
				this._applyTargetMarkerThicknessProperties(visId);
				// Apply all color set property values as denoted in VIPRConfig
				configuration && this._applyColorSetProperties(visId, configuration.config.include, configuration.isCustomVis);
			});
		},

		/**
		* @returns array of run time properties in a format usable by the properties panel.
		*/
		getProperties: function() {
			return this.whenVisControlReady()
				.then(() => {
					return VIPRProperties.getInstance().getProperties(
						this.viprWidget.properties,
						this.visualization.getSlots().getSlotList(),
						this.visualization.getDefinition().getId(),
						this.isPropertiesV2Enabled
					);
				}).then(properties => {
					//todo ???widget.getFeature? is it dead code??  livewidget_cleanup
					const propertiesFilter = this.visModel.ownerWidget.getFeature('visPropertiesFilter');
					return propertiesFilter ? propertiesFilter.filter(properties) : properties;
				});
		},

		getPropertyList: function() {
			return VIPRProperties.getInstance().getPropertyList(
				this.viprWidget.properties,
				this.visualization.getSlots().getSlotList(),
				this.content,
				this.isPropertiesV2Enabled
			);
		},

		getPropertyLayoutList: function() {
			return VIPRProperties.getInstance().getPropertyLayoutList(
				this.viprWidget.properties,
				this.visualization.getSlots().getSlotList(),
				this.content,
				this.dashboard,
				this.isPropertiesV2Enabled
			);
		},

		/**
		 * When a theme changes we expect that all the models and their theme definitions have been
		 * updated before we are notified. Then all we have to do is re-render.
		 *
		 * NOTE: Only Rave and VIPR Views have to do this as all other (current views) use css.
		 */
		onChangeTheme: function() {
			this.visModel.getRenderSequence().reRender();
		},


		/**
		* Set the specified property to the specified value
		* @param - info -> property of interest
		*	{
		*		id - property id,
		*		value - new value for the prop
		*	}
		*/
		setProperty: function(info) {
			VIPRProperties.getInstance().setProperty(this.viprWidget, info);
		},

		/**
		 * Generate thumbnail
		 *
		 * @param {Object} [options] - generate options
		 * @param {Object} [options.size] - aspect ratio to generate
		 * @param {number} [options.size.width=150] - aspect ratio width
		 * @param {number} [options.size.height=100] - aspect ratio height
		 * @param {string} [options.type=svg] - type of thumbnail to generate
		 *
		 * @return {Promise}
		 */
		generateThumbnail: function({ size = {}, type = 'svg' } = {}) {

			const {
				enabled = true
			} = this.getThumbnailConfig();

			let isMappingIncomplete = false;
			let isDisabled = !enabled;

			if (!this.isMappingComplete()) {
				isMappingIncomplete = true;
			}

			let promise;
			if (isDisabled || isMappingIncomplete) {
				promise = Promise.resolve();
			} else {

				const {
					type: configType,
					properties: configProperties = {}
				} = this.getThumbnailConfig();

				const {
					width,
					height
				} = size;

				if (configType) {
					type = configType;
				}

				const handler = this._createPredictHandler(VIPRCachedDataRequestHandler, this.predictHandler);

				promise = this.viprWidget.createHidden(width, height, type, true, IGNORED_THUMBNAIL_DECORATIONS, handler)
					.then((_hiddenViprWidgetInstance) => {

						const properties = Object.assign({}, DEFAULT_THUMBNAIL_PROPERTIES, configProperties);

						Object.keys(properties)
							.forEach((key) => {
								const value = properties[key];
								_hiddenViprWidgetInstance.setProperty(key, value);
							});

						return _hiddenViprWidgetInstance.render(type)
							.then((_renderOperation) => {
								let result;
								if (_renderOperation && _renderOperation.completed) {
									result = this.processThumbnailData(_renderOperation.data);
								}
								_hiddenViprWidgetInstance.destroy();
								return result;
							});
					});
			}

			return promise
				.then(thumbnail => ({
					thumbnail,
					isMappingIncomplete,
					isDisabled
				}));
		},

		processThumbnailData: function(data) {
			let result;
			if (data instanceof Blob) {
				result = new Promise((resolve, reject) => {
					try {
						const reader = new FileReader();
						let altThumbnailImage = stringResources.get('loadedThumbnailImage', { visType: this.visModel.getDefinition().label });
						reader.addEventListener('loadend', () => {
							resolve(`<img src="${reader.result}" alt="${altThumbnailImage}" />`);
						});
						reader.readAsDataURL(data);
					} catch(error) {
						reject(error);
					}
				});
			} else {
				result = Promise.resolve(data);
			}
			return result;
		},

		/**
		 * Thumbnail config
		 *
		 * @return {Object} thumbnail config
		 */
		getThumbnailConfig: function() {
			const {
				thumbnail = {}
			} = VIPRConfig.getConfig(this.visAPI.getVisId()) || {};

			return thumbnail;
		}
	});

	return VIPRView;
});
